import { isNumber, mean } from 'lodash';
import { DEFAULT_RANGE } from './constants';
import Term from './Term';

class Range {
  /**
   * A range indicates a value associated with a period of time
   * bound by two numbers, start and end.
   * It's invalid if either of those values are not numbers,
   * or if they are not in order.
   *
   * Name can be anything including a word index.
   * @param params
   */

  constructor(params) {
    Object.assign(this, params);
  }

  get name() {
    return this._name || DEFAULT_RANGE;
  }

  set name(name) {
    this._name = name;
  }

  get start() {
    return isNumber(this._start) ? this._start : null;
  }

  set start(value) {
    this._start = value;
  }

  get end() {
    return isNumber(this._end) ? this._end : null;
  }

  get mid() {
    if (!this.isValid) return null;
    return mean([this.start, this.end]);
  }

  placeOf(t) {
    if (!this.isValid) return null;
    if (!isNumber(t)) return null;
    if (t <= this.start) return t - this.start;
    if (t >= this.end) return this.end - t;
    return 0;
  }

  set end(value) {
    this._end = value;
  }

  get isValid() {
    return isNumber(this.start) && isNumber(this.end) && this.start <= this.end;
  }

  intersects(term) {
    if (!(this.isValid && (term instanceof Term) && term.isValidTime)) {
      return false;
    }

    return this.start < term.end && this.end >= term.start;
  }
}

/**
 * finds the nearest range in a series of ranges, given a time t
 * the assumption is that the ranges may NOT be contiguous, or even
 * necessarily, in order
 * @param ranges {Range}
 * @param t
 * @param trust
 * @returns {*}
 */
Range.nearestRange = (ranges, t, trust) => {
  let validRanges;
  if (trust) validRanges = ranges;
  else {
    if (!isNumber(t)) throw new Error('non-numeric index search for time');
    validRanges = ranges.filter(r => r instanceof Range && r.isValid);
  }

  let found = false;
  let nearest = null;
  let nearestDist = Number.MAX_VALUE;

  validRanges.forEach((range) => {
    if (found) return;
    const dist = range.placeOf(t);
    found = !dist;
    if (found) {
      nearest = range;
    } else {
      const absDist = Math.abs(dist);
      if (!nearest || (absDist < nearestDist)) {
        nearestDist = absDist;
        nearest = range;
      }
    }
  });

  return { nearest, nearestDist, found };
};

export default Range;
